"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCreditsContext } from "@/contexts/CreditsContext";
import { getPurchaseContext, trackFunnelEvent, trackPurchase } from "@/libs/ga";
import config from "@/config";
import { findPlanByPriceId, billingIntervalForPriceId, displayPriceForInterval } from "@/libs/stripePlans";

interface PaymentResultModalProps {
  onClose?: () => void;
}

const PaymentResultModal = ({ onClose }: PaymentResultModalProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { refreshCredits } = useCreditsContext();
  const [isVisible, setIsVisible] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState<boolean | null>(null);

  useEffect(() => {
    const paymentResult = searchParams.get("payment_success");

    if (paymentResult === "true") {
      setPaymentSuccess(true);
      setIsVisible(true);
      document.body.style.overflow = 'hidden';

      const purchaseContext = getPurchaseContext(true);
      trackFunnelEvent('monetization', 'purchase_completed', {
        ...purchaseContext,
        return_url: typeof window !== 'undefined' ? window.location.href : '',
        success: true,
      });

      const priceId = purchaseContext?.price_id as string | undefined;
      const plan = findPlanByPriceId(priceId);
      const value =
        purchaseContext?.value ??
        (plan && priceId
          ? displayPriceForInterval(plan, billingIntervalForPriceId(plan, priceId))
          : undefined);

      if (value != null) {
        trackPurchase({
          transaction_id: purchaseContext?.transaction_id ?? `peeqd_${Date.now()}`,
          value,
          currency: purchaseContext?.currency ?? "USD",
          package_type: purchaseContext?.package_type ?? purchaseContext?.mode,
          purchase_trigger: purchaseContext?.purchase_trigger,
          credits: purchaseContext?.credits,
        });
      }

      refreshCredits();

        // Trigger confetti animation
        if (typeof window !== "undefined") {
          import("canvas-confetti").then((confetti) => {
            // Create custom canvas with specific z-index
            const canvas = document.createElement('canvas');
            canvas.style.position = 'fixed';
            canvas.style.top = '0';
            canvas.style.left = '0';
            canvas.style.width = '100vw';
            canvas.style.height = '100vh';
            canvas.style.pointerEvents = 'none';
            canvas.style.zIndex = '10000'; // Above overlay (9999) but below modal (10001)
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            document.body.appendChild(canvas);

            const myConfetti = confetti.create(canvas, {
              resize: true,
              useWorker: true
            });

            const defaults = {
              spread: 360,
              ticks: 50,
              gravity: 0,
              decay: 0.94,
              startVelocity: 30,
              colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8']
            };

            function shoot() {
              myConfetti({
                ...defaults,
                particleCount: 40,
                scalar: 1.2,
                shapes: ['star']
              });

              myConfetti({
                ...defaults,
                particleCount: 10,
                scalar: 0.75,
                shapes: ['circle']
              });
            }

            setTimeout(shoot, 0);
            setTimeout(shoot, 100);
            setTimeout(shoot, 200);

            // Clean up canvas after animation
            setTimeout(() => {
              if (canvas && canvas.parentNode && canvas.parentNode.contains(canvas)) {
                try {
                  canvas.parentNode.removeChild(canvas);
                } catch (e) {
                  // Silently ignore if already removed
                  console.debug('Confetti cleanup: element already removed');
                }
              }
            }, 3000);
          });
        }
    } else if (paymentResult === "false") {
      setPaymentSuccess(false);
      setIsVisible(true);
      document.body.style.overflow = 'hidden';

      const purchaseContext = getPurchaseContext(true);
      trackFunnelEvent('monetization', 'purchase_cancelled', {
        ...purchaseContext,
        return_url: typeof window !== 'undefined' ? window.location.href : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleClose = () => {
    setIsVisible(false);
    document.body.style.overflow = 'unset';
    // Strip payment_success query param from the current URL so the modal
    // doesn't re-trigger on rerender.
    const params = new URLSearchParams(searchParams.toString());
    params.delete("payment_success");
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname);
    onClose?.();
  };

  // Cleanup effect to re-enable scrolling if component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!isVisible || paymentSuccess === null) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center p-4">
        {/* Modal */}
        <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 transform transition-all duration-300 scale-100 z-[10001] relative">
          {paymentSuccess ? (
            // Success Modal
            <div className="p-8 text-center">
              {/* Success Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Payment Successful
              </h3>
              <p className="text-gray-600 mb-6">
                Welcome to {config.appName}. Your payment went through and your account is ready to use.
              </p>

              <button
                onClick={handleClose}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-5 px-6 rounded-lg transition-colors duration-200"
              >
                Continue
              </button>
            </div>
          ) : (
            // Failure Modal
            <div className="p-8 text-center">
              {/* Error Icon */}
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
                <svg
                  className="h-8 w-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Payment Canceled
              </h3>
              <p className="text-gray-600 mb-6">
                No charges were made to your account. Please try again or contact support at: <strong>{config.resend.supportEmail}</strong>
              </p>

              <button
                onClick={handleClose}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-5 px-6 rounded-lg transition-colors duration-200"
              >
                Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentResultModal;
